export const calculateAge = (dateOfBirth: string | null): number => {
    if (!dateOfBirth) return 0;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  export const calculateAgeOnDate = (dateOfBirth: string | null, referenceDate: string): number => {
    if (!dateOfBirth) return 0;
    const birthDate = new Date(dateOfBirth);
    const refDate = new Date(referenceDate);
    let age = refDate.getFullYear() - birthDate.getFullYear();
    const monthDifference = refDate.getMonth() - birthDate.getMonth();
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && refDate.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  export const calculateAgeAtYearEnd = (dateOfBirth: string | null, year: number): number => {
    if (!dateOfBirth) return 0;
    const referenceDate = new Date(year, 11, 31); // 31st December of the given year
    return calculateAgeOnDate(dateOfBirth, referenceDate.toISOString());
  };

  export const yearsUntilAge = (dateOfBirth: string | null, targetAge: number): number => {
  if (!dateOfBirth) return 0;
  const currentAge = calculateAge(dateOfBirth);
  const remaining = targetAge - currentAge;
  return remaining > 0 ? remaining : 0;
};

  export const calculateRemainingMonths = (start: string, end: string): number | null => {
    if (!start || !end) return null;
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) - 1;
      return months >= 0 ? months : null;
    } catch {
      return null;
    }
  };

  /**
   * מחשב את ההפרש בשנים וחודשים מהיום עד תאריך עתידי
   * @param futureDate תאריך עתידי בפורמט ISO (YYYY-MM-DD)
   * @returns אובייקט עם years ו-months, או null אם התאריך לא תקין או בעבר
   */
  export const calculateTimeUntilDate = (futureDate: string): { years: number; months: number } | null => {
    if (!futureDate) return null;
    try {
      const today = new Date();
      const targetDate = new Date(futureDate);

      // אם התאריך בעבר
      if (targetDate <= today) return null;

      let years = targetDate.getFullYear() - today.getFullYear();
      let months = targetDate.getMonth() - today.getMonth();

      // תיקון אם החודשים שליליים
      if (months < 0) {
        years--;
        months += 12;
      }

      // תיקון נוסף לפי היום בחודש
      if (targetDate.getDate() < today.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }

      return { years, months };
    } catch {
      return null;
    }
  };

  /**
   * ממיר את התוצאה של calculateTimeUntilDate למחרוזת קריאה בעברית
   * @param futureDate תאריך עתידי בפורמט ISO (YYYY-MM-DD)
   * @returns מחרוזת בעברית עם התיאור, או null
   */
  export const formatTimeUntilDate = (futureDate: string): string | null => {
    const timeUntil = calculateTimeUntilDate(futureDate);
    if (!timeUntil) return null;

    const { years, months } = timeUntil;

    if (years === 0 && months === 0) {
      return "פחות מחודש";
    }

    if (years === 0) {
      return `${months} ${months === 1 ? 'חודש' : 'חודשים'}`;
    }

    if (months === 0) {
      return `${years} ${years === 1 ? 'שנה' : 'שנים'}`;
    }

    const yearsText = `${years} ${years === 1 ? 'שנה' : 'שנים'}`;
    const monthsText = `${months} ${months === 1 ? 'חודש' : 'חודשים'}`;
    return `${yearsText} ו-${monthsText}`;
  };

  /**
   * מחשב כמה זמן עבר מאז תאריך בעבר
   * @param pastDate תאריך בעבר בפורמט ISO (YYYY-MM-DD)
   * @returns אובייקט עם years ו-months, או null אם התאריך לא תקין או בעתיד
   */
  export const calculateTimeSinceDate = (pastDate: string): { years: number; months: number } | null => {
    if (!pastDate) return null;
    try {
      const today = new Date();
      const targetDate = new Date(pastDate);

      // אם התאריך בעתיד
      if (targetDate >= today) return null;

      let years = today.getFullYear() - targetDate.getFullYear();
      let months = today.getMonth() - targetDate.getMonth();

      // תיקון אם החודשים שליליים
      if (months < 0) {
        years--;
        months += 12;
      }

      // תיקון נוסף לפי היום בחודש
      if (today.getDate() < targetDate.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }

      return { years, months };
    } catch {
      return null;
    }
  };

  /**
   * ממיר את התוצאה של calculateTimeSinceDate למחרוזת קריאה בעברית
   * @param pastDate תאריך בעבר בפורמט ISO (YYYY-MM-DD)
   * @returns מחרוזת בעברית עם התיאור, או null
   */
  export const formatTimeSinceDate = (pastDate: string): string | null => {
    const timeSince = calculateTimeSinceDate(pastDate);
    if (!timeSince) return null;

    const { years, months } = timeSince;

    if (years === 0 && months === 0) {
      return "פחות מחודש";
    }

    if (years === 0) {
      return `${months} ${months === 1 ? 'חודש' : 'חודשים'}`;
    }

    if (months === 0) {
      return `${years} ${years === 1 ? 'שנה' : 'שנים'}`;
    }

    const yearsText = `${years} ${years === 1 ? 'שנה' : 'שנים'}`;
    const monthsText = `${months} ${months === 1 ? 'חודש' : 'חודשים'}`;
    return `${yearsText} ו-${monthsText}`;
  };
